import styled, { css } from 'styled-components'
import { SPACING, BORDER_RADIUS, TYPOGRAPHY, FONT_WEIGHT } from '../../../../styles/theme'
import { QuestionLabel, InputWrapper, interactiveBase, selectedStyle } from './shared'

interface MultipleChoiceQuestionProps {
  question: string
  value: string | null
  options: string[]
  onChange: (value: string) => void
}

const OptionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.sm};
  width: 100%;
`

const OptionCard = styled.button<{ $selected: boolean }>`
  ${interactiveBase}
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${SPACING.md};
  padding: ${SPACING.md} ${SPACING.lg};
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.regular};
  text-align: left;
  border-width: 1px;
  border-style: solid;
  border-radius: ${BORDER_RADIUS.lg};
  min-height: 48px;

  ${({ $selected }) =>
    $selected &&
    css`
      ${selectedStyle}
      font-weight: ${FONT_WEIGHT.medium};
    `}
`

const RadioDot = styled.span<{ $selected: boolean }>`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid
    ${({ $selected }) => ($selected ? 'var(--color-primary)' : 'var(--color-border-dark)')};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: border-color 0.15s ease;

  &::after {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${({ $selected }) => ($selected ? 'var(--color-primary)' : 'transparent')};
    transition: background 0.15s ease;
  }
`

const OptionText = styled.span`
  flex: 1;
  line-height: 1.4;
`

export function MultipleChoiceQuestion({
  question,
  value,
  options,
  onChange,
}: MultipleChoiceQuestionProps) {
  return (
    <InputWrapper>
      <QuestionLabel>{question}</QuestionLabel>
      <OptionList role="group" aria-label={question}>
        {options.map((opt) => (
          <OptionCard
            key={opt}
            type="button"
            $selected={value === opt}
            onClick={() => onChange(opt)}
            aria-pressed={value === opt}
          >
            <RadioDot $selected={value === opt} />
            <OptionText>{opt}</OptionText>
          </OptionCard>
        ))}
      </OptionList>
    </InputWrapper>
  )
}
